import React from 'react';
import { KenBurnsEffect, KenBurnsStep, SlideState, SlideTransition } from '../types';
import { KB_EFFECTS, MOTION_PRESETS, PLAYBACK_SPEEDS, TRANSITIONS } from '../constants';

export interface MotionTabProps {
  slide: SlideState;
  /** Ken Burns and motion presets are still-image ideas; clips trim and change speed instead. */
  isClip: boolean;
  slideIndex: number;
  durationIsFixed: boolean;
  enabledKBSteps: KenBurnsStep[];
  totalKBDuration: number;
  updateActive: (patch: Partial<SlideState>) => void;
  toggleKBEffect: (effect: KenBurnsEffect) => void;
  updateKBDuration: (effect: KenBurnsEffect, duration: number) => void;
}

const MotionTab: React.FC<MotionTabProps> = ({
  slide,
  isClip,
  slideIndex,
  durationIsFixed,
  enabledKBSteps,
  totalKBDuration,
  updateActive,
  toggleKBEffect,
  updateKBDuration,
}) => {
  const trimIn = slide.trimIn ?? 0;
  const trimOut = slide.trimOut ?? slide.sourceDuration ?? 0;
  const trimMax = slide.sourceDuration ?? 0;
  const trimmedLength = Math.max(0, trimOut - trimIn);

  return (
    <>
      {/* Duration */}
      <div className="se-field">
        <label className="se-label">Duration</label>
        <div className="se-duration-modes">
          <label className={`se-dur-option ${slide.durationMode === 'fixed' ? 'se-dur-active' : ''} ${!durationIsFixed ? 'se-dur-disabled' : ''}`}>
            <input
              type="radio"
              name={`dur-${slideIndex}`}
              checked={slide.durationMode === 'fixed'}
              onChange={() => updateActive({ durationMode: 'fixed' })}
              disabled={!durationIsFixed}
            />
            <span>Fixed</span>
          </label>
          <label className={`se-dur-option ${slide.durationMode === 'narration' ? 'se-dur-active' : ''}`}>
            <input
              type="radio"
              name={`dur-${slideIndex}`}
              checked={slide.durationMode === 'narration'}
              onChange={() => updateActive({ durationMode: 'narration' })}
            />
            <span>Match Narration</span>
          </label>
          <label className={`se-dur-option ${slide.durationMode === 'audio' ? 'se-dur-active' : ''}`}>
            <input
              type="radio"
              name={`dur-${slideIndex}`}
              checked={slide.durationMode === 'audio'}
              onChange={() => updateActive({ durationMode: 'audio' })}
            />
            <span>Match Audio</span>
          </label>
        </div>
        {slide.durationMode === 'fixed' && durationIsFixed && (
          <div className="se-dur-fixed">
            <input
              type="number"
              min={1}
              max={60}
              value={slide.fixedDuration}
              onChange={e => updateActive({ fixedDuration: parseInt(e.target.value) || 5 })}
            />
            <span>seconds</span>
          </div>
        )}
        {!durationIsFixed && (
          <div className="se-dur-hint">
            ℹ️ Duration automatically calculated from audio configuration
          </div>
        )}
        {isClip && (
          <div className="se-dur-hint">
            ℹ️ A clip slide runs for its trimmed length ÷ playback speed unless a mode above overrides it
          </div>
        )}
      </div>

      {isClip ? (
        <>
          {/* Trim — clip slides only */}
          <div className="se-field">
            <label className="se-label">Trim</label>
            <div className="se-trim-row">
              <label className="se-trim-field">
                <span>In (s)</span>
                <input
                  type="number"
                  min={0}
                  max={trimMax || undefined}
                  step={0.1}
                  value={trimIn}
                  onChange={e => {
                    const next = Math.max(0, parseFloat(e.target.value) || 0);
                    updateActive({ trimIn: Math.min(next, trimOut) });
                  }}
                />
              </label>
              <label className="se-trim-field">
                <span>Out (s)</span>
                <input
                  type="number"
                  min={0}
                  max={trimMax || undefined}
                  step={0.1}
                  value={trimOut}
                  onChange={e => {
                    const next = Math.max(0, parseFloat(e.target.value) || 0);
                    updateActive({ trimOut: Math.max(next, trimIn) });
                  }}
                />
              </label>
              <button
                className="se-btn-inline"
                onClick={() => updateActive({ trimIn: 0, trimOut: slide.sourceDuration ?? 0 })}
                title="Reset trim to the full clip"
              >
                Reset
              </button>
            </div>
            <div className="se-help-text">
              {trimMax > 0
                ? `${trimmedLength.toFixed(1)}s of ${trimMax.toFixed(1)}s`
                : 'ℹ️ Clip length is read once the video has been processed'}
            </div>
          </div>

          {/* Playback speed — clip slides only */}
          <div className="se-field">
            <label className="se-label">Playback Speed</label>
            <select
              className="se-select"
              value={slide.playbackSpeed}
              onChange={e => updateActive({ playbackSpeed: parseFloat(e.target.value) })}
            >
              {PLAYBACK_SPEEDS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </>
      ) : (
        <>
          {/* Motion Preset */}
          <div className="se-field">
            <label className="se-label">Motion Preset</label>
            <div className="se-preset-grid">
              {MOTION_PRESETS.map(p => (
                <button
                  key={p.value}
                  className={`se-preset-btn ${slide.motionPreset === p.value ? 'se-preset-active' : ''}`}
                  onClick={() => updateActive({ motionPreset: p.value })}
                >
                  <span className="se-preset-icon">{p.icon}</span>
                  <span className="se-preset-name">{p.label}</span>
                  <span className="se-preset-desc">{p.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Ken Burns Effects - Only show when Custom preset selected */}
          {slide.motionPreset === 'custom' && (
            <div className="se-field">
              <label className="se-label">Ken Burns Effect</label>
              <div className="se-kb-list">
                {KB_EFFECTS.map(kb => {
                  const step = slide.kenBurnsSteps.find(s => s.effect === kb.value);
                  const isEnabled = step?.enabled || false;
                  return (
                    <div key={kb.value} className={`se-kb-row ${isEnabled ? 'se-kb-active' : ''}`}>
                      <label className="se-kb-check">
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => toggleKBEffect(kb.value)}
                        />
                        <span>{kb.icon} {kb.label}</span>
                      </label>
                      {isEnabled && (
                        <div className="se-kb-dur">
                          <input
                            type="number"
                            min={1}
                            max={30}
                            value={step?.duration || 3}
                            onChange={e => updateKBDuration(kb.value, parseInt(e.target.value) || 1)}
                          />
                          <span>s</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {enabledKBSteps.length > 1 && (
                <div className="se-kb-total">Total: {totalKBDuration}s across {enabledKBSteps.length} effects</div>
              )}
            </div>
          )}
        </>
      )}

      {/* Slide Transition */}
      <div className="se-field">
        <label className="se-label">Slide Transition</label>
        <select
          className="se-select"
          value={slide.transition}
          onChange={e => updateActive({ transition: e.target.value as SlideTransition })}
        >
          {TRANSITIONS.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        {slide.transition !== 'none' && (
          <div className="se-dur-fixed" style={{ marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Duration (s)</span>
              <input
                type="number"
                min={0.05}
                max={10}
                step={0.05}
                value={slide.transitionDuration ?? ''}
                onChange={e => updateActive({ transitionDuration: e.target.value ? parseFloat(e.target.value) : undefined })}
                placeholder="auto"
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Offset (s)</span>
              <input
                type="number"
                min={0}
                max={120}
                step={0.1}
                value={slide.transitionOffset ?? ''}
                onChange={e => updateActive({ transitionOffset: e.target.value ? parseFloat(e.target.value) : undefined })}
                placeholder="auto"
              />
            </label>
          </div>
        )}
      </div>
    </>
  );
};

export default MotionTab;
